/**
 * UUID Utility Functions
 * 
 * Provides functions to validate and clean UUIDs according to
 * FRONTEND_UUID_GUIDELINES.md requirements.
 */

/**
 * UUID regex pattern (standard UUID v4 format)
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Validate UUID format
 * @param uuid - UUID to validate
 * @returns True if valid UUID format
 */
export function isValidUUID(uuid: string | null | undefined): boolean {
  if (!uuid || typeof uuid !== 'string') {
    return false;
  }
  
  return UUID_REGEX.test(uuid);
}

/**
 * Clean UUID by removing common prefixes
 * Note: While the backend handles prefixes, it's best practice to send clean UUIDs
 * @param id - ID that might have prefixes
 * @returns Clean UUID
 */
export function cleanUUID(id: string | null | undefined): string | null {
  if (!id || typeof id !== 'string') {
    return id || null;
  }
  
  // Remove common prefixes (case-insensitive)
  const cleaned = id.replace(/^(table-|booking-|branch-|area-)/i, '');
  
  // Validate the cleaned ID is a valid UUID
  if (UUID_REGEX.test(cleaned)) {
    return cleaned;
  }
  
  // If it was already a valid UUID, return it
  if (UUID_REGEX.test(id)) {
    return id;
  }
  
  // If cleaning didn't help, return original (might not be a UUID)
  return id;
}

/**
 * Extract and validate booking ID from various sources
 * @param source - Can be a booking object, table object, or direct ID
 * @returns Clean booking ID or null if invalid
 */
export function extractBookingId(source: any): string | null {
  if (!source) {
    return null;
  }
  
  // If it's already a string, validate and clean it
  if (typeof source === 'string') {
    const cleaned = cleanUUID(source);
    return isValidUUID(cleaned) ? cleaned : null;
  }
  
  // If it's an object, try to get the ID
  if (typeof source === 'object') {
    const id = source.id || source.bookingId || source.booking?.id;
    if (id && typeof id === 'string') {
      const cleaned = cleanUUID(id);
      return isValidUUID(cleaned) ? cleaned : null;
    }
  }
  
  return null;
}

/**
 * Extract and validate table ID from various sources
 * @param source - Can be a table object or direct ID
 * @returns Clean table ID or null if invalid
 */
export function extractTableId(source: any): string | null {
  if (!source) {
    return null;
  }
  
  // If it's already a string, validate and clean it
  if (typeof source === 'string') {
    const cleaned = cleanUUID(source);
    return isValidUUID(cleaned) ? cleaned : null;
  }
  
  // If it's an object, try to get the ID
  if (typeof source === 'object') {
    const id = source.id || source.tableId || source.table?.id;
    if (id && typeof id === 'string') {
      const cleaned = cleanUUID(id);
      return isValidUUID(cleaned) ? cleaned : null;
    }
  }
  
  return null;
}

